import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";

// Material UI
import Rating from "@mui/material/Rating";
import Icon from "@mui/material/Icon";
// Components
import MDBox from "components/MDBox";
import MDBadgeDot from "components/MDBadgeDot";
import MDButton from "components/MDButton";
import ConfirmDialog from "components/ConfirmDialog";
import { LojaPageContainer, LojaDataGrid } from "../common";
// Interfaces
import { IProdutoLoja } from "interfaces/loja";
// Services
import { listarProdutosLoja, deletarProdutoLoja } from "services/lojaProduto";
// Context
import { useMaterialUIController, setMessage } from "context";
// Helpers
import { formatCurrency } from "helpers/utils";
import { PaginatedResponse } from "interfaces/paginatedResponse";
import { Autocomplete, Pagination, Typography } from "@mui/material";
import MDInput from "components/MDInput";

export default function LojaProdutos() {
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();
  const [rows, setRows] = useState<PaginatedResponse<IProdutoLoja>>({
    data: [],
    meta: { totalRecord: 0, pageCurrent: 1, pageSize: 0, totalPages: 0 },
  });
  const [pageCurrent, setPageCurrent] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<{
    open: boolean;
    id?: string | number;
    nome?: string;
  }>({
    open: false,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { result, status } = await listarProdutosLoja({
          page: pageCurrent,
          limit: limit,
        });
        // Retorno com sucesso
        if (status === 200) setRows(result);
      } catch (error: any) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: error?.message || "Falha ao listar produtos.",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [pageCurrent, limit]);

  const columns: GridColDef<IProdutoLoja>[] = useMemo(
    () => [
      { field: "nome", headerName: "Produto", flex: 1, minWidth: 240 },
      {
        field: "preco",
        headerName: "Preço",
        width: 180,
        renderCell: (params) => {
          const preco =
            params.row.variacoes.find((v: any) => v.isPadrao)?.preco ?? 0;

          const precoOriginal =
            params.row.variacoes.find((v: any) => v.isPadrao)?.precoOriginal ??
            null;
          if (precoOriginal && params.row.emPromocao) {
            return (
              <div>
                <div
                  style={{
                    textDecoration: "line-through",
                    color: "#888",
                    fontSize: 12,
                  }}
                >
                  {formatCurrency(precoOriginal)}
                </div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(preco)}</div>
              </div>
            );
          }
          return <span>{formatCurrency(preco)}</span>;
        },
      },
      {
        field: "emPromocao",
        headerName: "Promoção",
        width: 140,
        renderCell: (params) => (
          <MDBadgeDot
            color={params.row.emPromocao ? "success" : "light"}
            size="lg"
            badgeContent=""
          />
        ),
      },
      {
        field: "emDestaque",
        headerName: "Destaque",
        width: 140,
        renderCell: (params) => (
          <MDBadgeDot
            color={params.row.emDestaque ? "success" : "light"}
            size="lg"
            badgeContent=""
          />
        ),
      },
      {
        field: "mediaAvaliacoes",
        headerName: "Média de avaliações",
        width: 140,
        renderCell: (params) => (
          <Rating
            name="half-rating-read"
            defaultValue={params.row.mediaAvaliacoes}
            precision={0.5}
            readOnly
          />
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => (
          <MDBadgeDot
            color={params.value === "ATIVO" ? "success" : "error"}
            size="md"
            badgeContent={params.value.toUpperCase()}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Ações",
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <MDBox display="flex" gap={1}>
            <MDButton
              size="medium"
              variant="text"
              title="Editar"
              color="dark"
              onClick={() => navigate(`/loja/produtos/editar/${params.row.id}`)}
            >
              <Icon>edit</Icon>
            </MDButton>
            <MDButton
              size="medium"
              variant="text"
              title="Excluir"
              circular={true}
              color="error"
              onClick={() =>
                setConfirm({
                  open: true,
                  id: params.row.id,
                  nome: params.row.nome,
                })
              }
            >
              <Icon>delete</Icon>
            </MDButton>
          </MDBox>
        ),
      },
    ],
    []
  );

  return (
    <LojaPageContainer
      title="Loja / Produtos"
      subtitle="Gerencie os produtos da sua loja."
      actions={
        <MDBox>
          <MDButton
            variant="gradient"
            color="dark"
            size="medium"
            onClick={() => navigate("/loja/produtos/create")}
          >
            <Icon>add</Icon>&nbsp;Novo Produto
          </MDButton>
        </MDBox>
      }
    >
      <LojaDataGrid
        rows={rows.data}
        getRowId={(r) => r.id}
        columns={columns}
        loading={loading}
        hideFooter
        hideFooterPagination
        autoHeight={true}
        localeText={{
          noRowsLabel: "Nenhum produto cadastrado",
        }}
        disableRowSelectionOnClick
      />

      <MDBox
        display="flex"
        flexDirection={{ xs: "column-reverse", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        my={2}
      >
        <Autocomplete
          defaultValue="10"
          options={["10", "50", "100"]}
          getOptionLabel={(option) => option}
          renderInput={(params) => <MDInput {...params} variant="outlined" />}
          onChange={(e, value) => setLimit(Number(value))}
        />
        <Typography variant="body2">
          total de produtos: {rows.meta.totalRecord}
        </Typography>
        <Pagination
          count={rows.meta.totalPages}
          page={rows.meta.pageCurrent}
          onChange={(e, page) => setPageCurrent(page)}
          showFirstButton
          showLastButton
          color="primary"
        />
      </MDBox>

      <ConfirmDialog
        open={confirm.open}
        title="Confirmar"
        message="Confirmar exclusão permanente?"
        onClose={() => setConfirm({ open: false })}
        onConfirm={async () => {
          if (!confirm.id) return;
          try {
            const id = String(confirm.id);
            setRows((prev) => ({
              ...prev,
              data: prev.data.filter((r) => String(r.id) !== id),
            }));
            await deletarProdutoLoja(id);
            setMessage(dispatch, {
              open: true,
              severity: "success",
              text: "Produto excluído.",
            });
          } catch (e: any) {
            setMessage(dispatch, {
              open: true,
              severity: "error",
              text: e?.message || "Falha ao excluir.",
            });
            const { result, status } = await listarProdutosLoja();
            if (status === 200)
              setRows(
                Array.isArray((result as any)?.data)
                  ? (result as any).data
                  : (result as any)
              );
          } finally {
            setConfirm({ open: false });
          }
        }}
      />
    </LojaPageContainer>
  );
}
